import { useHistory, useLocation } from "react-router-dom";
import { cn } from "../../utils/bem";
import { Menu } from "../Menu/Menu";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./SidebarMenu.prefix.css";

// Normalize a menuItem into { label, href }. The Menu.Builder accepts
// either a tuple `[path, label]`, a raw page component with
// `{ menuItem, title, path }` fields, or functions — we ignore the
// function form here (rare and not used in settings menus).
const toNavEntry = (item, basePath) => {
  if (Array.isArray(item)) {
    const [pagePath, pageLabel] = item;
    return { label: pageLabel, href: `${basePath}${pagePath}`.replace(/\/+/g, "/") };
  }
  const { menuItem, title, path } = item ?? {};
  if (typeof path !== "string") return null;
  return {
    label: title ?? menuItem,
    href: `${basePath}${path}`.replace(/\/+/g, "/"),
  };
};

export const SidebarMenu = ({ children, menu, path, menuItems, search }) => {
  const { t } = useTranslation();
  const rootClass = cn("sidebar-menu");
  const history = useHistory();
  const location = useLocation();

  // Narrow-viewport dropdown alternative to the horizontal pill strip.
  // CSS toggles which one is visible at the ``.sidebar-menu__select``
  // breakpoint; this keeps the full list accessible on phones without
  // requiring a sideways swipe to find the current section.
  const navEntries =
    Array.isArray(menuItems) && menuItems.length > 1
      ? menuItems
          .filter((m) => m && m !== "SPACER" && m !== "DIVIDER")
          .map((m) => toNavEntry(m, path))
          .filter(Boolean)
      : [];
  const activeEntry =
    navEntries.find((e) => location.pathname === e.href) ??
    navEntries.find((e) => location.pathname.startsWith(`${e.href}/`)) ??
    navEntries[0];

  return (
    <div className={rootClass}>
      {menuItems && menuItems.length > 1 ? (
        <>
          <div className={rootClass.elem("navigation").toClassName()}>
            {/* Optional search slot — a search box that jumps to the right
                section (used by Project Settings). Rendered above the rail
                so it's the first thing a lost user sees. */}
            {search}
            <Menu>{menuItems ? Menu.Builder(path, menuItems) : menu}</Menu>
          </div>
          {navEntries.length > 0 && (
            <div className={rootClass.elem("select").toClassName()}>
              {search}
              <select
                value={activeEntry?.href ?? ""}
                onChange={(e) => history.push(e.target.value)}
                aria-label={t("components.sidebarmenu.sidebar_menu.settings_section")}
              >
                {navEntries.map((e) => (
                  <option key={e.href} value={e.href}>
                    {e.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      ) : null}
      <div className={rootClass.elem("content").toClassName()}>{children}</div>
    </div>
  );
};
